"use client";

import { signIn } from "next-auth/react";
import { SiSimplelogin, SiGoogle } from "@icons-pack/react-simple-icons";

import { Button } from "~/components/ui/button";
import { Input } from "../ui/input";
import { useForm, type SubmitHandler } from "react-hook-form";
import { TypographyMuted } from "../ui/typography";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useId, useState } from "react";
import { capturePostHogEvent } from "~/lib/posthog/client";
import Logo from "../logo";
import { Label } from "../ui/label";

type Inputs = {
  email: string;
};

type LoginScreenProps = {
  callbackUrl?: string | null;
  role?: string | null;
  showDevAdminLogin?: boolean;
};

export function LoginScreen({
  callbackUrl = null,
  role = null,
  showDevAdminLogin = false,
}: LoginScreenProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const [authMethod, setAuthMethod] = useState<"google" | "email" | null>(null);
  const emailId = useId();
  const emailHintId = useId();
  const emailErrorId = useId();

  useEffect(() => {
    if (role === "therapist") {
      localStorage.setItem("intendedRole", "therapist");
    }
  }, [role]);

  const defaultCallbackUrl = callbackUrl || "/app/login";

  const onSubmit: SubmitHandler<Inputs> = async ({ email }) => {
    setAuthMethod("email");
    try {
      await signIn("http-email", {
        callbackUrl: defaultCallbackUrl,
        email,
      });

      capturePostHogEvent("login", { provider: "email" });
    } finally {
      setAuthMethod(null);
    }
  };

  const onGoogleLogin = async () => {
    setAuthMethod("google");
    try {
      await signIn("google", {
        callbackUrl: defaultCallbackUrl,
      });

      capturePostHogEvent("login", { provider: "google" });
    } finally {
      setAuthMethod(null);
    }
  };

  return (
    <div className="relative isolate flex min-h-dvh w-full items-start justify-center bg-secondary px-4 py-7 sm:items-center sm:py-10">
      <Image
        src="/images/brand/login-window-light-v2.webp"
        alt=""
        fill
        sizes="100vw"
        loading="eager"
        className="pointer-events-none -z-10 object-cover object-center"
      />
      <div className="grid w-full max-w-sm gap-4 sm:gap-5">
        <div className="flex justify-center">
          <Link href="/" aria-label="Početna stranica">
            <Logo surface="light" />
          </Link>
        </div>
        <div className="grid gap-5 rounded-lg border bg-background p-5 shadow-sm sm:p-6">
          <div className="grid gap-2 text-center">
            <h1 className="text-3xl font-bold">
              {role === "therapist"
                ? "Registracija za terapeute"
                : "Prijavi se"}
            </h1>
            <p className="text-balance text-muted-foreground">
              {role === "therapist"
                ? "Postani deo našeg tima psihoterapeuta"
                : "Uđi u svoj nalog ili nastavi sa zakazivanjem."}
            </p>
          </div>
          <div className="grid gap-4">
            <Button
              onClick={onGoogleLogin}
              variant="cta"
              className="w-full focus-visible:ring-brand-action"
              icon={SiGoogle}
              isLoading={authMethod === "google"}
              disabled={authMethod !== null}
            >
              Nastavi preko Google naloga
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-3 text-muted-foreground">
                  Ili nastavi sa
                </span>
              </div>
            </div>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid gap-2"
              noValidate
            >
              <Label htmlFor={emailId} className="sr-only">
                Email adresa
              </Label>
              <Input
                id={emailId}
                data-testid="email"
                type="email"
                placeholder="Email adresa"
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? emailErrorId : emailHintId}
                className="focus-visible:ring-brand-action"
                {...register("email", {
                  required: "Unesi email adresu.",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Unesi ispravnu email adresu.",
                  },
                })}
              />
              {errors.email?.message ? (
                <p id={emailErrorId} className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              ) : null}
              <Button
                data-testid="send-magic-link"
                variant="secondary"
                className="w-full border border-input focus-visible:ring-brand-action"
                type="submit"
                icon={SiSimplelogin}
                isLoading={authMethod === "email"}
                disabled={authMethod !== null}
              >
                Pošalji link za prijavu
              </Button>
              <p
                id={emailHintId}
                className="text-center text-xs text-muted-foreground"
              >
                Poslaćemo ti link za prijavu. Nema lozinke.
              </p>
            </form>
          </div>
          <TypographyMuted className="text-center text-xs">
            Ako nastaviš, prihvataš{" "}
            <Link
              href="/legal/uslovi-koriscenja"
              className="text-brand underline decoration-brand/40 underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-action focus-visible:ring-offset-2"
            >
              uslove korišćenja
            </Link>
            . Tvoje podatke koristimo u skladu sa{" "}
            <Link
              href="/legal/politika-privatnosti"
              className="text-brand underline decoration-brand/40 underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-action focus-visible:ring-offset-2"
            >
              politikom privatnosti
            </Link>
            .
          </TypographyMuted>
          {showDevAdminLogin ? (
            <form action="/api/dev-auth/login-as-admin" method="post">
              <Button
                data-testid="dev-admin-login"
                variant="ghost"
                className="w-full focus-visible:ring-brand-action"
                type="submit"
              >
                Admin prijava (dev/preview)
              </Button>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}
